'use client';
import React, { useEffect, useRef, useState } from 'react';
import useInterval from './hooks/useInterval';
import { BreakingNews } from '@benzinga/basic-news-manager';
import Marquee from 'react-fast-marquee';
import { usePageVisibility } from '@benzinga/hooks';
import { checkOverflow } from '@benzinga/frontend-utils';
import styles from './BreakingNewsBanner.module.scss';

export interface BreakingNewsBannerProps {
  data: BreakingNews[];
  onClose?: (nid: string) => void;
  progressBarColor?: string;
  textColor?: string;
}

export interface BannerText {
  link?: string;
  title: string;
}

const SECONDS = 5;

const BANNER_COLORS = {
  breaking: '#e02424',
  event: '#057a55',
  news: '#3f83f8',
  notice: '#ff5a1f',
};

export const BreakingNewsBanner: React.FC<BreakingNewsBannerProps> = ({
  data,
  progressBarColor = '#2e4129',
  textColor = '#ffffff',
}) => {
  const [index, setIndex] = useState(0);
  const [shownEvent, setShownEvent] = useState<BreakingNews>(data[0]);
  const [progressBarTransitionDuration, setProgressBarTransitionDuration] = useState<number>(1);
  const [seconds, setSeconds] = useState(SECONDS);
  const [isOverflown, setIsOverflown] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const hiddenBannerRef = useRef<HTMLDivElement>(null);
  const bannerTextContainerRef = useRef<HTMLDivElement>(null);

  const isVisible = usePageVisibility();

  useInterval(() => {
    if (data.length <= 1) return;
    const bannerTextContainer = bannerTextContainerRef.current;
    if (!bannerTextContainer) return;

    setShownEvent(data[index]);

    const fadeToNextItem = () => {
      if (isOverflown) setIsOverflown(false);
      bannerTextContainer.classList.remove('show');
      setTimeout(() => {
        setIndex((index + 1) % data.length);
        setShownEvent(data[index]);
      }, 500);
      setTimeout(() => {
        bannerTextContainer.classList.add('show');
        const element = hiddenBannerRef.current;
        if (!element) return;
        const isOverflowing = checkOverflow(element);
        if (isOverflowing) setIsOverflown(true);
      }, 1000);
    };

    if (isVisible) {
      if (progressBarTransitionDuration === 0) {
        setProgressBarTransitionDuration(1);
      }
      setSeconds(seconds - 1);
      if (seconds === 0) {
        setProgressBarTransitionDuration(0);
        setSeconds(SECONDS);
      } else if (seconds === 1) {
        fadeToNextItem();
      }
    }
  }, 1000);

  useEffect(() => {
    const element = hiddenBannerRef.current;
    if (!element) return;

    const isOverflowing = checkOverflow(element);
    setIsOverflown(isOverflowing);

    const onResize = () => {
      const isOverflowing = checkOverflow(element);
      setIsOverflown(isOverflowing);
    };
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (data.length === 0) return null;

  const progressBarWidth = Math.round(seconds * (100 / SECONDS));
  const backgroundColor = BANNER_COLORS[shownEvent?.type || 'news'];

  return (
    <div className="flex flex-col w-full breaking-news-banner" id="breaking-news-banner" ref={ref}>
      <div className={`${styles.bannerContainer} flex flex-col h-12 relative shadow-md`} style={{ backgroundColor }}>
        <div
          className="banner-text show flex font-bold h-full items-center justify-center w-full whitespace-nowrap"
          ref={bannerTextContainerRef}
          style={{ color: textColor }}
        >
          {isOverflown ? (
            <Marquee gradient={false} speed={70}>
              <div className="mx-4">{shownEvent.title}</div>
            </Marquee>
          ) : (
            <div>{shownEvent.title}</div>
          )}
        </div>
        {shownEvent.link && (
          <a
            className="block absolute w-full h-full top-0 left-0 cursor-pointer"
            data-action={`Breaking News Banner Click - ${shownEvent.type}`}
            href={shownEvent.link}
            rel="noreferrer"
            style={{ color: textColor, zIndex: 2 }}
            target="_blank"
          >
            <div className="sr-only">Visit {shownEvent.link}</div>
          </a>
        )}
        {data.length > 1 && (
          <div
            className={styles.progressBar}
            style={
              {
                '--progress-color': progressBarColor,
                '--progress-duration': `${progressBarTransitionDuration}s`,
                '--progress-width': `${progressBarWidth}%`,
              } as React.CSSProperties
            }
          />
        )}
      </div>
      <div className={`${styles.hiddenBanner} hidden-banner`} ref={hiddenBannerRef}>
        <div>{shownEvent.title}</div>
      </div>
    </div>
  );
};
